import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { BenefitParticipationStatus } from 'core'
import { UserToTerminate } from 'core/application/adminDashboard/managePeople/viewPeople/terminateEmployee'
import { ProductIds } from 'core/application/commons/config'
import { EmployeeDetailsResponse } from 'core/application/companies/getEmployeeDetails'

import useCurrentProduct from 'common/hooks/useCurrentProduct'

import { parseZoneFormat } from 'common/dateFormatters'
import { mapEnumObjectValues } from 'common/enumFormatters'

import { Alert, Button, Card, Category, Col, Icon, Item, Row, Tooltip } from '..'
import TerminateUserModal from '../../../adminDashboard/managePeople/common/termination/TerminateUserModal'
import UserAvatar from './UserAvatar'

import './EmployeeDetails.less'

export type EmployeeProfileDetailsProps = {
  employeeDetails: EmployeeDetailsResponse
  profileTitle: ReactNode
  editProfileUrl: string
}

const EmployeeProfileDetails = ({ editProfileUrl, employeeDetails, profileTitle }: EmployeeProfileDetailsProps) => {
  const { t: translate } = useTranslation()
  const [terminateUserVisible, setTerminateUserVisible] = useState<boolean>(false)
  const [userToTerminate, setUserToTerminate] = useState<UserToTerminate>()
  const history = useHistory()
  const product = useCurrentProduct()

  const getFieldValue = (value: any) =>
    value ? (
      <p className="mb-0">{value}</p>
    ) : (
      <p className="gray-6 mb-0">{translate('admin-dashboard.manage-people.complete-profile.not-set')}</p>
    )

  const displayTerminateUserModal = (id: string, fullName: string) => {
    setUserToTerminate({ employeeId: id, fullName: fullName } as UserToTerminate)
    setTerminateUserVisible(true)
  }

  return (
    employeeDetails && (
      <>
        {employeeDetails.canBeTerminated && userToTerminate && (
          <TerminateUserModal
            visible={terminateUserVisible}
            onCancel={() => {
              setUserToTerminate(undefined)
              setTerminateUserVisible(false)
            }}
            afterSuccessTerminate={() => {
              history.goBack()
              setUserToTerminate(undefined)
              setTerminateUserVisible(false)
            }}
            userToTerminate={userToTerminate}
          />
        )}

        <Row>
          <Col span={24}>
            <Card title={profileTitle}>
              <Row>
                <Col md={8} className="flex align-center lg-justify-end pr-16">
                  <UserAvatar firstName={employeeDetails.firstName} lastName={employeeDetails.lastName} />
                </Col>
                <Col md={16} className="flex align-center left_border p-16">
                  <span className="gray-7">
                    {translate('admin-dashboard.manage-people.complete-profile.eligibility-header')}
                  </span>

                  {employeeDetails && employeeDetails?.productClass?.id ? (
                    <span className="pr-8">
                      <Icon className="far fa-check-circle color-green-6 mr-4 ml-8" />
                      &nbsp;{translate('admin-dashboard.manage-people.complete-profile.eligible')}
                    </span>
                  ) : employeeDetails && employeeDetails?.employmentStatus?.id ? (
                    <span className="pr-8">
                      <Icon className="far fa-times color-red-6 mr-4 ml-8" />
                      &nbsp;{translate('admin-dashboard.manage-people.complete-profile.not-eligible')}
                    </span>
                  ) : (
                    <span className="pr-8">
                      <Icon className="far fa-info-circle color-yellow-6 mr-4 ml-8" />
                      &nbsp;{translate('admin-dashboard.manage-people.complete-profile.not-set')}
                    </span>
                  )}

                  {product.productId === ProductIds.IchraId && employeeDetails && (
                    <>
                      <span className="gray-7 ml-8">
                        {translate('admin-dashboard.manage-people.complete-profile.pk-status-header')}
                      </span>
                      <span className="pr-8 ml-4">{employeeDetails?.status}</span>
                      <span className="gray-7 ml-8">
                        {translate(
                          'admin-dashboard.manage-people.complete-profile.current-benefit-participation-status',
                        )}
                      </span>
                      <span className="pr-8 ml-4">
                        {mapEnumObjectValues(BenefitParticipationStatus)[employeeDetails?.benefitParticipationStatus]}
                      </span>
                    </>
                  )}

                  {product.productId === ProductIds.GroupHraId && (
                    <Tooltip
                      title={translate('admin-dashboard.manage-people.complete-profile.ghra-eligibility-status')}
                      position="bottom"
                    >
                      <Icon type="far fa-question-circle" className="info_icon" fontAwesome />
                    </Tooltip>
                  )}
                  {
                    // TODO: Enable later
                    // product.productId === ProductIds.Perks
                    false && (
                      <Tooltip
                        title={translate('admin-dashboard.manage-people.complete-profile.perks-eligibility-status')}
                        position="bottom"
                      >
                        <Icon type="far fa-question-circle" className="info_icon" fontAwesome />
                      </Tooltip>
                    )
                  }
                </Col>
                <Col md={24} className="flex justify-end lg-justify-center align-center">
                  <div className="flex">
                    {employeeDetails.canBeTerminated && (
                      <Button
                        className="m-8"
                        icon="fa fa-times-circle"
                        danger
                        onClick={() =>
                          displayTerminateUserModal(
                            employeeDetails.id ? employeeDetails.id : '',
                            employeeDetails.firstName,
                          )
                        }
                      >
                        {translate('admin-dashboard.manage-people.view-employee.terminate-button')}
                      </Button>
                    )}

                    {employeeDetails.canBeEdited && (
                      <Button
                        type="primary"
                        icon="fa fa-pencil-alt"
                        className="m-8"
                        onClick={() => {
                          history.push(editProfileUrl)
                        }}
                      >
                        {translate('general.edit')}
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Card>
          <SectionBlock
            className="mb-24"
            titleElement={
              <>
                <Icon className="far fa-user mr-8" />
                <Category title={translate('admin-dashboard.manage-people.complete-profile.header-1')} />
              </>
            }
          >
            <Row>
              <Col sm={24} md={12} className="section-block_content-item">
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.manage-people.edit-profile.first-name-label')}
                  text={employeeDetails.firstName}
                />
              </Col>
              <Col sm={24} md={12} className="section-block_content-item">
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.manage-people.edit-profile.last-name-label')}
                  text={employeeDetails.lastName}
                />
              </Col>
              <Col sm={24} md={12} className="section-block_content-item">
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.manage-people.edit-profile.personal-email-label')}
                  text={employeeDetails.personalEmail}
                />
              </Col>
              <Col sm={24} md={12} className="section-block_content-item">
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.manage-people.edit-profile.work-email-label')}
                  text={employeeDetails.workEmail}
                />
              </Col>
              <Col sm={24} md={12} className="section-block_content-item">
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.manage-people.edit-profile.state-of-residence')}
                  text={employeeDetails.stateOfResidence?.name}
                />
              </Col>
              <Col sm={24} md={12} className="section-block_content-item">
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.manage-people.edit-profile.birthdate')}
                  text={employeeDetails?.birthday ? parseZoneFormat(employeeDetails.birthday) : ''}
                />
              </Col>
              <Col span={24}>
                <Row>
                  <Col sm={24} md={12} className="section-block_content-item">
                    <Item
                      translate={translate}
                      title={translate('admin-dashboard.manage-people.edit-profile.ein-label')}
                      text={employeeDetails.identificationNumber}
                    />
                  </Col>
                  <Col sm={24} md={12} className="section-block_content-item">
                    <Item
                      translate={translate}
                      title={translate('admin-dashboard.manage-people.edit-profile.zip-code-label')}
                      text={employeeDetails.zipCode}
                    />
                  </Col>
                </Row>
              </Col>
              <Col sm={24} md={12} className="section-block_content-item mb-24">
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.manage-people.edit-profile.receive-sms-label')}
                  text={employeeDetails.receiveSms ? 'Yes' : 'No'}
                />
              </Col>
              <Col sm={24} md={12} className="section-block_content-item mb-24">
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.manage-people.edit-profile.phone-number-label')}
                  text={employeeDetails.workPhone}
                />
              </Col>
            </Row>
          </SectionBlock>

          <SectionBlock
            className="mb-24"
            titleElement={
              <>
                <Icon className="far fa-suitcase mr-8" />
                <Category title={translate('admin-dashboard.manage-people.complete-profile.employment-details')} />
              </>
            }
          >
            <Row>
              <Col span={24} className="section-block_content-item">
                <Alert
                  type="info"
                  showIcon
                  message={translate('admin-dashboard.manage-people.complete-profile.alert-header')}
                  description={translate('admin-dashboard.manage-people.complete-profile.alert-content')}
                />
              </Col>
              <Col span={24} className="section-block_content-item">
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.manage-people.edit-profile.hire-date-label')}
                  text={employeeDetails.hireDate ? parseZoneFormat(employeeDetails.hireDate) : ''}
                />
              </Col>

              <Col span={24} className="section-block_content-item">
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.manage-people.edit-profile.termination-date-label')}
                  text={employeeDetails.terminatedDate ? parseZoneFormat(employeeDetails.terminatedDate) : ''}
                />
              </Col>

              <Col span={24} className="section-block_content-item">
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.manage-people.edit-profile.pk-status')}
                  text={employeeDetails?.status || ''}
                />
              </Col>
              <Col span={24} className="section-block_content-item">
                <Item
                  translate={translate}
                  title={translate('admin-dashboard.manage-people.edit-profile.employment-status-label')}
                  text={employeeDetails?.employmentStatus?.name || ''}
                />
              </Col>
              {employeeDetails && employeeDetails?.productClass?.name && (
                <Col span={24} className="section-block_content-item">
                  <Item
                    translate={translate}
                    title={translate('admin-dashboard.manage-people.edit-profile.employee-class-label')}
                    text={employeeDetails.productClass.name}
                  />
                </Col>
              )}

              {employeeDetails && employeeDetails?.benefitEligibilityDate && (
                <Col span={24} className="section-block_content-item mb-24">
                  <div>
                    <div className="flex">
                      <p className="gray-7 pr-8 mb-4">
                        {translate('admin-dashboard.manage-people.edit-profile.benefit-eligibility-date-label')}
                      </p>

                      {product.productId === ProductIds.GroupHraId && (
                        <Tooltip
                          title={translate('admin-dashboard.manage-people.complete-profile.ghra-eligibility-date')}
                          position="top"
                        >
                          <Icon type="far fa-info-circle" className="info_icon" fontAwesome />
                        </Tooltip>
                      )}
                      {product.productId === ProductIds.Perks && (
                        <Tooltip
                          title={translate('admin-dashboard.manage-people.complete-profile.perks-eligibility-date')}
                          position="top"
                        >
                          <Icon type="far fa-info-circle" className="info_icon" fontAwesome />
                        </Tooltip>
                      )}
                    </div>
                    <p className="mb-0">{getFieldValue(parseZoneFormat(employeeDetails.benefitEligibilityDate))}</p>
                  </div>
                </Col>
              )}
            </Row>
          </SectionBlock>

          {employeeDetails.shouldDisplayFamilyStatus && (
            <SectionBlock
              titleElement={
                <>
                  <Icon className="far fa-users mr-8" />
                  <Category title={translate('admin-dashboard.manage-people.complete-profile.header-3')} />
                </>
              }
            >
              <Row>
                <Col sm={24} md={12} className="section-block_content-item mb-24">
                  <Item
                    translate={translate}
                    title={translate('admin-dashboard.manage-people.edit-profile.family-status-label')}
                    text={employeeDetails.familyStatus ? employeeDetails.familyStatus.name : null}
                  />
                </Col>
              </Row>
            </SectionBlock>
          )}
        </Card>
      </>
    )
  )
}

export default EmployeeProfileDetails

interface SectionBlockProps {
  titleElement: React.ReactNode
  children: React.ReactNode
  className?: string
  mobileOnly?: boolean
}

export const SectionBlock = ({ titleElement, children, className = '', mobileOnly = false }: SectionBlockProps) => {
  return (
    <div className={`section-block ${className}`}>
      <Row>
        <Col md={24} lg={mobileOnly ? 24 : 8}>
          <div className="section-block_title-wrap">{titleElement}</div>
        </Col>
        <Col md={24} lg={mobileOnly ? 24 : 16}>
          {children}
        </Col>
      </Row>
    </div>
  )
}
